<template>
  <main class="config-compare-wrapper">
    <section name="select-node-and-version-card">
      <b-card>
        <span class="header__label">Configuration Comparison</span>
        <div class="row mt-2">
          <!-- ? Baseline Configuration Start -->
          <div class="col-12 col-md-6">
            <div class="select-node-and-version-box">
              <div class="d-flex justify-content-between align-items-center">
                <label class="box__header--label">Baseline Configuration</label>
                <atom-button
                  buttonId="button-view"
                  buttonStyle="border"
                  :isDisable="isDisabledViewBaselineConfigButton"
                  @click="viewConfig('baseline')"
                  >View</atom-button
                >
              </div>
              <atom-select
                v-model="currentBaselineNode"
                title="Node Name"
                placeholder="Select Node (IP Address)"
                label="nodeValue"
                class="mt-1"
                :isAsterisk="true"
                :isClearable="false"
                :isAppendToBody="false"
                :isEnableProper="false"
                :option="nodeOptions"
              ></atom-select>
              <atom-select
                v-model="currentBaselineNodeVersion"
                title="Version"
                placeholder="Select Version"
                class="mt-1"
                :isAsterisk="true"
                :isClearable="false"
                :isAppendToBody="false"
                :isEnableProper="false"
                :isDisable="!currentBaselineNode"
                :option="baselineVersionOptions"
              ></atom-select>
              <div
                v-if="selectedBaselineTimestamp"
                class="mt-1 d-flex justify-content-end align-items-center"
              >
                <span class="custom-small-text">Timestamp: </span>
                <span class="ml-1"
                  ><b>{{ formatDateTime(selectedBaselineTimestamp) }}</b></span
                >
              </div>
            </div>
          </div>
          <!-- ? Baseline Configuration End -->

          <!-- ? Compare To Configuration Start -->
          <div class="col-12 col-md-6 mt-2 mt-md-0">
            <div class="select-node-and-version-box">
              <div class="d-flex justify-content-between align-items-center">
                <label class="box__header--label">Compare To</label>
                <atom-button
                  buttonId="button-view"
                  buttonStyle="border"
                  :isDisable="isDisabledViewCompareToConfigButton"
                  @click="viewConfig('compare-to')"
                  >View</atom-button
                >
              </div>
              <atom-select
                v-model="currentCompareToNode"
                title="Node Name"
                placeholder="Select Node (IP Address)"
                label="nodeValue"
                class="mt-1"
                :isAsterisk="true"
                :isClearable="false"
                :isAppendToBody="false"
                :isEnableProper="false"
                :isDisable="true"
                :option="nodeOptions"
              ></atom-select>
              <atom-select
                v-model="currentCompareToNodeVersion"
                title="Version"
                placeholder="Select Version"
                class="mt-1"
                :isAsterisk="true"
                :isClearable="false"
                :isAppendToBody="false"
                :isEnableProper="false"
                :isDisable="!currentCompareToNode"
                :option="compareToVersionOptions"
              ></atom-select>
              <div
                v-if="selectedCompareToTimestamp"
                class="mt-1 d-flex justify-content-end align-items-center"
              >
                <span class="custom-small-text">Timestamp: </span>
                <span class="ml-1"
                  ><b>{{ formatDateTime(selectedCompareToTimestamp) }}</b></span
                >
              </div>
            </div>
          </div>
          <!-- ? Compare To Configuration End -->
        </div>
      </b-card>
    </section>

    <section
      v-if="isShowDiffCompareSection"
      name="show-difference-configuration"
    >
      <b-card>
        <div v-if="$vssWidth > 767" class="row mt-2">
          <div class="col-6 text-center">
            <span class="compare-configuration__header--label">Baseline</span>
          </div>
          <div class="col-6 text-center">
            <span class="compare-configuration__header--label">Compare To</span>
          </div>
        </div>
        <div v-else class="text-center mt-2">
          <span class="compare-configuration__header--label"
            >Configuration Difference</span
          >
        </div>
        <div class="w-100 mt-1">
          <code-diff
            language="Plaintext"
            :old-string="currentBaselineConfigData"
            :new-string="currentCompareToConfigData"
            :output-format="codeDiffOutputFormat"
            :noDiffLineFeed="true"
            :context="10"
            @before-render="setShowLoading(true)"
            @after-render="setShowLoading(false)"
          />
        </div>
      </b-card>
    </section>
    <footer style="height: 12px"></footer>
    <!-- ? Modal Section Start -->
    <molecule-modal-dialog
      modalId="modal-configuration-detail"
      :textTitle="`${targetNodeData.node} (version ${targetNodeData.version})`"
      :textSubtitle="`Timestamp: ${formatDateTime(targetNodeData.timestamp)}`"
      :isButtonLeft="false"
      :isButtonRight="false"
      :isSlotBody="true"
      :isCloseOnBackDrop="false"
      @clickButtonRight="onClickCloseFromModal"
    >
      <template #body>
        <div class="mt-1 w-95">
          <div
            name="topic-and-button-wrapper"
            class="d-flex justify-content-between align-items-end mb-2"
          >
            <div name="LHS">
              <span class="modal-topic--text">Configuration Detail</span>
            </div>
            <div name="RHS" style="display: flex">
              <atom-button
                buttonId="copy-generated-config"
                buttonStyle="border"
                class="mr-1"
                @click="onClickCopyConfigDetail"
              >
                <span v-if="screenWidth > 500" class="button-text">Copy</span>
                <feather-icon v-else icon="CopyIcon"></feather-icon>
              </atom-button>
              <atom-button
                buttonId="download-generated-config"
                buttonStyle="border"
                @click="onClickDownloadConfigDetail"
              >
                <span v-if="screenWidth > 500" class="button-text"
                  >Download</span
                >
                <feather-icon v-else icon="DownloadIcon"></feather-icon>
              </atom-button>
            </div>
          </div>
          <div name="generated-config__wrapper">
            <prism-editor
              v-model="targetNodeData.config"
              class="text-area-with-line-number custom-max-height"
              readonly
              :lineNumbers="screenWidth > 1080 ? true : false"
              :highlight="highlighter"
            ></prism-editor>
          </div>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- ? Modal Section End -->
    <textarea
      v-on:focus="$event.target.select()"
      readonly
      ref="copyConfigCompareTextBox"
      class="hidden-textbox"
      :value="targetNodeData.config"
    />
  </main>
</template>

<script>
// import Prism Editor
import { PrismEditor } from "vue-prism-editor"
import "vue-prism-editor/dist/prismeditor.min.css"

// import highlighting library (you can use any library you want just return html string)
import { highlight, languages } from "prismjs/components/prism-core"
import "prismjs/components/prism-clike"
import "prismjs/components/prism-javascript"
import "prismjs/themes/prism-tomorrow.css" // import syntax highlighting styles

import { BCard } from "bootstrap-vue"
import { CodeDiff } from "v-code-diff"
import { DateTime } from "luxon"
import AtomButton from "@/components/atoms/AtomButton.vue"
import AtomSelect from "@/components/atoms/AtomSelect.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import VueScreenSize from "vue-screen-size"
import mixins from "@/mixins"
import mixinsValidator from "@/mixins/mixinsValidator"
export default {
  name: "OrganismConfigurationComparison",
  components: {
    AtomButton,
    AtomSelect,
    BCard,
    CodeDiff,
    PrismEditor,
    MoleculeModalDialog,
  },
  props: {
    nodeOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    baselineVersionOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    compareToVersionOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    selectedBaselineNode: {
      type: Object,
      required: false,
      default: () => {},
    },
    selectedBaselineNodeVersion: {
      type: Object,
      required: false,
      default: () => {},
    },
    selectedBaselineTimestamp: {
      type: String,
      required: false,
      default: "",
    },
    selectedBaselineConfigData: {
      type: String,
      required: false,
      default: "",
    },
    selectedCompareToNode: {
      type: Object,
      required: false,
      default: () => {},
    },
    selectedCompareToTimestamp: {
      type: String,
      required: false,
      default: "",
    },
    selectedCompareToNodeVersion: {
      type: Object,
      required: false,
      default: () => {},
    },
    selectedCompareToConfigData: {
      type: String,
      required: false,
      default: "",
    },
  },
  mixins: [mixins, mixinsValidator, VueScreenSize.VueScreenSizeMixin],
  emits: ["updateSelectedBaselineNodeData", "updateSelectedCompareToNodeData"],
  data() {
    return {
      currentBaselineNode: null,
      currentBaselineNodeVersion: null,
      currentBaselineTimestamp: "",
      currentBaselineConfigData: "",
      currentCompareToNode: null,
      currentCompareToNodeVersion: null,
      currentCompareToTimestamp: "",
      currentCompareToConfigData: "",
      targetNodeData: {
        nodeName: "",
        version: 0,
        timestamp: "",
        config: "",
      },
    }
  },
  computed: {
    screenWidth() {
      return this.$vssWidth
    },
    codeDiffOutputFormat() {
      return this.$vssWidth < 768 ? "line-by-line" : "side-by-side"
    },
    isShowDiffCompareSection() {
      return (
        this.currentBaselineNode &&
        this.currentBaselineNodeVersion &&
        this.currentCompareToNode &&
        this.currentCompareToNodeVersion
      )
    },
    isDisabledViewBaselineConfigButton() {
      return !this.currentBaselineNode || !this.currentBaselineNodeVersion
    },
    isDisabledViewCompareToConfigButton() {
      return !this.currentCompareToNode || !this.currentCompareToNodeVersion
    },
  },
  watch: {
    /* SET CURRENT FROM URL PATH START*/
    selectedBaselineNode: {
      deep: true,
      handler() {
        if (this.selectedBaselineNode) {
          this.currentBaselineNode = this.selectedBaselineNode
        }
      },
    },
    selectedBaselineNodeVersion: {
      deep: true,
      handler() {
        if (this.selectedBaselineNodeVersion) {
          this.currentBaselineNodeVersion = this.selectedBaselineNodeVersion
        }
      },
    },
    selectedBaselineTimestamp: {
      deep: true,
      handler() {
        if (this.selectedBaselineTimestamp) {
          this.currentBaselineTimestamp = this.selectedBaselineTimestamp
        }
      },
    },
    selectedBaselineConfigData: {
      deep: true,
      handler() {
        this.currentBaselineConfigData = this.selectedBaselineConfigData
      },
    },
    selectedCompareToNode: {
      deep: true,
      handler() {
        if (this.selectedCompareToNode) {
          this.currentCompareToNode = this.selectedCompareToNode
        }
      },
    },
    selectedCompareToNodeVersion: {
      deep: true,
      handler() {
        if (this.selectedCompareToNodeVersion) {
          this.currentCompareToNodeVersion = this.selectedCompareToNodeVersion
        }
      },
    },
    selectedCompareToTimestamp: {
      deep: true,
      handler() {
        if (this.selectedCompareToTimestamp) {
          this.currentCompareToTimestamp = this.selectedCompareToTimestamp
        }
      },
    },
    selectedCompareToConfigData: {
      deep: true,
      handler() {
        this.currentCompareToConfigData = this.selectedCompareToConfigData
      },
    },
    /* SET CURRENT FROM URL PATH END */

    /* UPDATE SELECTED FROM ORGANISM START */
    currentBaselineNode: {
      deep: true,
      handler(newValue, oldValue) {
        this.currentCompareToNode = JSON.parse(
          JSON.stringify(this.currentBaselineNode)
        )
        this.$emit("onUpdateSelectedBaselineNode", this.currentBaselineNode)
        if (newValue && oldValue && newValue !== oldValue) {
          this.currentBaselineNodeVersion = null
        }
      },
    },
    currentBaselineNodeVersion: {
      deep: true,
      handler() {
        if (this.currentBaselineNodeVersion) {
          this.$emit(
            "onUpdateSelectedBaselineNodeVersion",
            this.currentBaselineNodeVersion
          )
        }
      },
    },
    currentCompareToNode: {
      deep: true,
      handler(newValue, oldValue) {
        this.$emit("onUpdateSelectedCompareToNode", this.currentCompareToNode)
        if (newValue && oldValue && newValue !== oldValue) {
          this.currentCompareToNodeVersion = null
        }
      },
    },
    currentCompareToNodeVersion: {
      deep: true,
      handler() {
        if (this.currentCompareToNodeVersion) {
          this.$emit(
            "onUpdateSelectedCompareToNodeVersion",
            this.currentCompareToNodeVersion
          )
        }
      },
    },
    /* UPDATE SELECTED FROM ORGANISM END */
  },
  methods: {
    formatDateTime(datetime) {
      if (this.hasString(datetime)) {
        return DateTime.fromISO(datetime).toFormat("dd/MM/yyyy HH:mm:ss")
      } else {
        return datetime
      }
    },

    setShowLoading(status) {
      this.showLoading(status)
    },

    highlighter(code) {
      return code // languages.<insert language> to return html with markup
    },

    viewConfig(sourceData) {
      if (sourceData === "baseline") {
        this.targetNodeData.node = this.currentBaselineNode.nodeName
        this.targetNodeData.version = this.currentBaselineNodeVersion
        this.targetNodeData.timestamp = this.currentBaselineTimestamp
        this.targetNodeData.config = this.selectedBaselineConfigData
      } else {
        this.targetNodeData.node = this.currentCompareToNode.nodeName
        this.targetNodeData.version = this.currentCompareToNodeVersion
        this.targetNodeData.timestamp = this.currentCompareToTimestamp
        this.targetNodeData.config = this.selectedCompareToConfigData
      }
      this.openModal("modal-configuration-detail")
    },

    onClickCopyConfigDetail() {
      this.$refs.copyConfigCompareTextBox.focus()
      document.execCommand("copy")
      this.toastSuccess("Copy successfully")
    },

    onClickDownloadConfigDetail() {
      let blob = new Blob([this.targetNodeData.config], { type: "text/plain" })
      let link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `${this.targetNodeData.node}_version_${this.targetNodeData.version}.txt`
      link.click()
      this.toastSuccess("Download successfully")
    },

    onClickCloseFromModal() {
      this.closeModal()
    },

    // modal
    openModal(id) {
      this.modalId = id
      this.$bvModal.show(this.modalId)
    },

    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = null
    },
  },
}
</script>

<style lang="scss" scoped>
.header__label {
  font-weight: 500;
  font-size: 18px;
  color: #5e5873;
}

.modal-topic--text {
  font-size: 12px;
}

.select-node-and-version-box {
  padding: 1.5rem;
  border: 1px solid #b9b9c3;
  border-radius: 6px;
}

.box__header--label {
  font-weight: 600;
  font-size: 14px;
}

.compare-configuration__header--label {
  font-weight: 500;
  font-size: 18px;
  line-height: 22px;
}

.custom-max-height {
  max-height: 500px;
  overflow-y: auto;
}

.custom-small-text {
  font-size: 12px;
}

.hidden-textbox {
  position: absolute;
  top: -99999px;
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
  .modal-topic--text {
    font-size: 14px;
  }
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
}
</style>
